import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Loader2, CheckCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { requestPasswordReset } from "@/api/apiService";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // In development, fallback to mock if API is not available
      try {
        await requestPasswordReset(email);
      } catch (apiError) {
        console.warn('API request failed, using mock response:', apiError);
        // Simulate API delay in development
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      setSubmitted(true);
      toast.success("Reset link sent", {
        description: "Check your email for password reset instructions"
      });
    } catch (error) {
      toast.error("Request failed", {
        description: error instanceof Error ? error.message : "Please try again later"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background overflow-y-auto">
      <Header />
      <div className="flex-1 flex items-center justify-center p-4 mt-16">
        <div className="w-full max-w-md">
          <Card className="border-border">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-bold">Forgot Password</CardTitle>
              <CardDescription>
                Enter your email address and we'll send you a link to reset your password
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <div className="text-center py-4 space-y-4">
                  <CheckCircle className="h-12 w-12 text-primary mx-auto" />
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium">Reset Link Sent</h3>
                    <p className="text-muted-foreground">
                      We've sent a password reset link to <span className="font-medium">{email}</span>.
                      Please check your inbox and follow the instructions.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        placeholder="name@example.com"
                        type="email"
                        className="pl-10"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Reset Link
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
            <CardFooter className="flex justify-center">
              <div className="text-sm text-muted-foreground">
                <Link 
                  to="/login" 
                  className="text-primary hover:underline"
                >
                  Back to Login
                </Link>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForgotPassword;